import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Popper,
  Portal,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import Iconify from '@/components/iconify';
import { findAnchor, resolveSteps } from './spotlightAnchors';

// ----------------------------------------------------------------------

const HOLE_PADDING = 8;
const CARD_WIDTH = 372;
const INTRO_CARD_WIDTH = 460;

/** Enquanto o scroll suave acontece o recorte precisa perseguir a âncora. */
const FOLLOW_DURATION_MS = 700;

/** Respiro acima do recorte quando o elemento entra pelo topo da tela. */
const SCROLL_MARGIN = 96;

export const spotlightStepShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  /** Valor do atributo `data-tour` do elemento destacado. Ausente = passo centralizado. */
  anchor: PropTypes.string,
  title: PropTypes.string.isRequired,
  body: PropTypes.string,
  hint: PropTypes.string,
  /** Lista "antes → agora", usada no passo de fechamento. */
  changes: PropTypes.arrayOf(
    PropTypes.shape({ from: PropTypes.string, to: PropTypes.string })
  ),
  intro: PropTypes.bool,
});

// ----------------------------------------------------------------------

export default function SpotlightTour({ open, steps, onClose, onFinish }) {
  const theme = useTheme();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const [plan, setPlan] = useState([]);
  const [index, setIndex] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [rect, setRect] = useState(null);

  const cardRef = useRef(null);
  const restoreFocusRef = useRef(null);

  const step = plan[index] || null;
  const total = plan.length;
  const isLast = total > 0 && index === total - 1;

  // Resolve os passos um frame depois da abertura, quando as âncoras já pintaram.
  useEffect(() => {
    if (!open) {
      setPlan([]);
      setIndex(0);
      return undefined;
    }

    restoreFocusRef.current = document.activeElement;

    const frame = window.requestAnimationFrame(() => {
      setPlan(resolveSteps(steps));
      setIndex(0);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [open, steps]);

  // Segue a âncora: rola até ela e recalcula o recorte durante a animação.
  useEffect(() => {
    if (!open || !step) return undefined;

    const element = findAnchor(step.anchor);
    setAnchorEl(element);

    if (!element) {
      setRect(null);
      return undefined;
    }

    // Elemento mais alto que a tela entra pelo topo: centralizar rolaria até o
    // meio dele, e o começo — que é o que interessa — ficaria acima da dobra.
    const isTall = element.offsetHeight > window.innerHeight * 0.6;
    const previousScrollMargin = element.style.scrollMarginTop;

    if (isTall) element.style.scrollMarginTop = `${SCROLL_MARGIN}px`;

    element.scrollIntoView({
      block: isTall ? 'start' : 'center',
      behavior: reduceMotion ? 'auto' : 'smooth',
    });

    const update = () => setRect(element.getBoundingClientRect());
    update();

    let frame = null;
    const startedAt = performance.now();
    const follow = (now) => {
      update();
      if (now - startedAt < FOLLOW_DURATION_MS) frame = window.requestAnimationFrame(follow);
    };
    frame = window.requestAnimationFrame(follow);

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      element.style.scrollMarginTop = previousScrollMargin;
    };
  }, [open, step, reduceMotion]);

  useEffect(() => {
    if (!open || !step) return;

    cardRef.current?.focus({ preventScroll: true });
  }, [open, step]);

  const finish = useCallback(() => {
    restoreFocusRef.current?.focus?.({ preventScroll: true });
    onFinish();
  }, [onFinish]);

  const skip = useCallback(() => {
    restoreFocusRef.current?.focus?.({ preventScroll: true });
    onClose();
  }, [onClose]);

  const goNext = useCallback(() => {
    if (index >= plan.length - 1) {
      finish();
      return;
    }
    setIndex(index + 1);
  }, [index, plan.length, finish]);

  const goBack = useCallback(() => {
    setIndex((current) => Math.max(0, current - 1));
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        skip();
      }
      if (event.key === 'ArrowRight') goNext();
      if (event.key === 'ArrowLeft') goBack();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, skip, goNext, goBack]);

  const scrim = alpha(theme.palette.grey[900], 0.72);

  const holeSx = useMemo(() => {
    if (!rect) return null;

    return {
      position: 'fixed',
      top: rect.top - HOLE_PADDING,
      left: rect.left - HOLE_PADDING,
      width: rect.width + HOLE_PADDING * 2,
      height: rect.height + HOLE_PADDING * 2,
      borderRadius: 1.5,
      border: `2px solid ${theme.palette.primary.main}`,
      boxShadow: `0 0 0 9999px ${scrim}, 0 0 0 8px ${alpha(theme.palette.primary.main, 0.24)}`,
      pointerEvents: 'none',
      zIndex: theme.zIndex.modal + 1,
      // Sem transição de propósito: o recorte é recalculado quadro a quadro
      // enquanto a página rola, e uma transição faria ele arrastar atrás.
    };
  }, [rect, theme, scrim]);

  if (!open || !step) return null;

  const card = (
    <Paper
      ref={cardRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="spotlight-tour-title"
      sx={{
        width: step.intro ? INTRO_CARD_WIDTH : CARD_WIDTH,
        maxWidth: 'calc(100vw - 32px)',
        p: 3,
        borderRadius: 2,
        boxShadow: theme.customShadows.z24,
        outline: 'none',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <Typography variant="overline" color="primary.main" sx={{ flex: 1 }}>
          {index === 0 ? 'Novidade' : `Passo ${index + 1} de ${total}`}
        </Typography>

        <IconButton size="small" onClick={skip} aria-label="Fechar">
          <Iconify icon="eva:close-fill" width={18} />
        </IconButton>
      </Stack>

      <LinearProgress
        variant="determinate"
        value={((index + 1) / total) * 100}
        sx={{ height: 3, borderRadius: 1, mb: 2 }}
      />

      <Typography id="spotlight-tour-title" variant={step.intro ? 'h5' : 'h6'} gutterBottom>
        {step.title}
      </Typography>

      {step.body && (
        <Typography variant="body2" color="text.secondary">
          {step.body}
        </Typography>
      )}

      {step.changes?.length > 0 && (
        <Stack spacing={1} mt={2}>
          {step.changes.map((change) => (
            <Stack
              key={change.from}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                bgcolor: 'background.neutral',
              }}
            >
              <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>
                {change.from}
              </Typography>
              <Iconify icon="eva:arrow-forward-fill" width={14} sx={{ color: 'primary.main' }} />
              <Typography variant="caption" sx={{ flex: 1, fontWeight: 600, textAlign: 'right' }}>
                {change.to}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {step.hint && (
        <Stack
          direction="row"
          spacing={1}
          mt={2}
          sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
          }}
        >
          <Iconify
            icon="eva:bulb-outline"
            width={16}
            sx={{ color: 'primary.main', mt: '2px', flexShrink: 0 }}
          />
          <Typography variant="caption" color="text.secondary">
            {step.hint}
          </Typography>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" spacing={1} mt={3}>
        <Button size="small" color="inherit" onClick={skip} sx={{ color: 'text.secondary' }}>
          {isLast ? 'Fechar' : 'Pular'}
        </Button>

        <Box sx={{ flex: 1 }} />

        {index > 0 && (
          <Button size="small" color="inherit" variant="outlined" onClick={goBack}>
            Voltar
          </Button>
        )}

        <Button size="small" variant="contained" onClick={isLast ? finish : goNext}>
          {isLast ? 'Começar' : 'Próximo'}
        </Button>
      </Stack>
    </Paper>
  );

  return (
    <Portal>
      {/* Bloqueia interação com a tela durante o tour. O recorte não recebe
          eventos, então todo clique cai aqui. */}
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: theme.zIndex.modal + 1,
          ...(rect ? null : { bgcolor: scrim }),
        }}
      />

      {holeSx && <Box aria-hidden sx={holeSx} />}

      {anchorEl && rect ? (
        <Popper
          open
          anchorEl={anchorEl}
          placement="bottom-start"
          modifiers={[
            { name: 'offset', options: { offset: [0, 16] } },
            { name: 'preventOverflow', options: { padding: 16 } },
            { name: 'flip', options: { padding: 16 } },
          ]}
          sx={{ zIndex: theme.zIndex.modal + 2 }}
        >
          {card}
        </Popper>
      ) : (
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: theme.zIndex.modal + 2,
          }}
        >
          {card}
        </Box>
      )}
    </Portal>
  );
}

SpotlightTour.propTypes = {
  open: PropTypes.bool,
  steps: PropTypes.arrayOf(spotlightStepShape).isRequired,
  /** Saída sem concluir (X, Esc, "Pular"). */
  onClose: PropTypes.func.isRequired,
  /** Saída pelo último passo. */
  onFinish: PropTypes.func.isRequired,
};
